import { useEffect } from 'react';
import { useAuthStore } from '../store/authStore';
import { API_URL } from '../lib/api';

const TICK_MS = 15_000;      // cada 15 s se suma tiempo si la pestaña está visible (sin red)
const SEND_AFTER_S = 300;    // se envía al servidor cada 5 minutos de juego acumulado
const MIN_FLUSH_S = 10;      // al ocultar o cerrar la pestaña, se envía si hay al menos 10 s

// Suma tiempo de juego al perfil mientras la pestaña está visible.
// Acumula en el navegador y envía en lotes para gastar pocas invocaciones del servidor.
export function useHeartbeat() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);

  useEffect(() => {
    if (!isAuthenticated || !user || user.isGuest || !token) return;

    let pending = 0;
    let lastTick = Date.now();

    const send = (seconds: number) => {
      // keepalive: la petición sobrevive aunque la pestaña se esté cerrando
      fetch(`${API_URL}/api/users/me/heartbeat`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ seconds: Math.min(Math.round(seconds), 600) }),
      }).catch(() => {});
    };

    const tick = () => {
      const now = Date.now();
      if (document.visibilityState === 'visible') pending += (now - lastTick) / 1000;
      lastTick = now;
      if (pending >= SEND_AFTER_S) { send(pending); pending = 0; }
    };

    const flush = () => {
      tick();
      if (pending >= MIN_FLUSH_S) { send(pending); pending = 0; }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
      else lastTick = Date.now();
    };

    const id = setInterval(tick, TICK_MS);
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [isAuthenticated, user?.id, user?.isGuest, token]);
}
